// pfs-list.ts — <pfs-list>: self-contained, konfigurierbare Listen-WC (PFS-632).
//
// Der Schlussstein der Listen-Abstraktion — als ELEMENT, nicht Vererbung/Objekt
// (Pascal 2026-07-05: „Kompositionen und Adapter, bleib dynamisch" + self-contained).
// Statt in JEDER Cut-App-Liste dieselbe Maschine (Card→Toolbar→Table→Paginator,
// runLoad, syncTable, rerenderPreservingSearch, Such-Fokus-Erhalt) zu kopieren,
// lebt sie HIER einmal. Der Consumer reicht nur DATEN rein — kein start(), kein
// Shadow-Plumbing, keine Vererbung:
//   el.controller = ListController   — WIE geladen wird (fetcher/id/limit/filters)
//   el.config = () => ({ … })        — WAS gezeigt wird (Titel/Spalten/Toolbar)
//   el.gate = () => ({ ok, message })— optional: darf laden/zeigen (scoped/practiceId)
//   el.onError = () => …             — optional: Fehler-Feedback (Toast)
//   el.reload() / el.rerender()      — imperativ nach Domaenen-Mutationen
//
// Self-contained: eigener Shadow, TOKEN-gestylt (Card/Toolbar/Button-Chrome hier,
// nicht via geerbte Foundation-Klassen) → immun gegen WP/Tailwind/Foundation-Drift,
// wie jedes andere <pfs-*>. Kinder (data-table/paginator/scope-toggle/search-input)
// stylen sich selbst; diese WC layoutet nur. [[feedback_seams_not_flags]]
import { defineComponent } from "../../composition/define-component.js";
import { withShadow } from "../../composition/with-shadow.js";
import { SHADOW_RESET_CSS } from "../../styles/shadow-reset.js";
import { BUTTON_CSS } from "../../styles/button.js";
import { takeOwnProperty } from "../../utils/upgrade-property.js";
import type { ListController } from "../../list/list-controller.js";
import type { DataColumn, PfsDataTableElement } from "../data-table/pfs-data-table.js";
import type { PfsPaginatorElement } from "../paginator/pfs-paginator.js";
import type { PfsScopeToggleElement } from "../scope-toggle/pfs-scope-toggle.js";
import type { PfsSearchInputElement } from "../search-input/pfs-search-input.js";

/** Scope-/Lifecycle-Filter-Toggle: Optionen + welcher Filter-Key in F gesetzt wird. */
export interface ListScopeConfig<F> {
  options: [string, string][];
  filterKey: keyof F & string;
}

/** Toolbar-Bestueckung (alles optional — nur was gesetzt ist, rendert). */
export interface ListToolbarConfig<F> {
  add?: { label: string; onClick: () => void; variant?: string };
  scope?: ListScopeConfig<F>;
  search?: { placeholder: string };
  /** Zusatz-Filter-Controls (z.B. ein Benutzer-Picker) — rendern in DERSELBEN
   *  Toolbar-Zeile neben scope-toggle/search (kein Sonderweg je Liste). */
  filters?: HTMLElement[];
}

/** Deklarative Sicht einer Liste — die „WAS"-Naht (das „WIE" steckt im controller). */
export interface ListConfig<T, F> {
  title: string;
  subtitle?: string;
  /** Optionales Header-Icon (slotbares `<svg slot="icon">`) — Media-Object-Kachel
   *  im sektionierten Card-Header (PFS-760, MFA-Look). Fehlt es, bleibt die
   *  Icon-Kachel leer/versteckt; die Card ist trotzdem sektioniert (Divider). */
  icon?: string;
  columns: DataColumn<T>[];
  rowClass?: (item: T) => string;
  emptyIcon?: string;
  /** Dynamischer Empty-Titel (scope/such-abhaengig). */
  emptyTitle?: () => string;
  toolbar?: ListToolbarConfig<F>;
  loadingText?: string;
  /** Optionaler Seitengroessen-Waehler im Paginator, z.B. "25,50,100". */
  pageSizes?: string;
  /** View-Modus: false/undefined (Default) = Seiten-Paginator; true = Endless-
   *  Scroll (data-table-Sentinel + loadMore statt Paginator). Orthogonal zur
   *  loadStrategy des Controllers: "page" → server-gestreamt (loadMore fetcht),
   *  "all"/"parallel"/"drain" → alles geladen, loadMore enthuellt nur. */
  endless?: boolean;
}

/** Ergebnis von gate(): darf die Liste laden/anzeigen? Sonst Platzhalter mit message. */
export interface ListGate {
  ok: boolean;
  message?: string;
}

/** Typisierte Consumer-Sicht auf <pfs-list> — Consumer setzen
 *  `(createElement("pfs-list") as PfsListElement<Row,F>).controller = …`. */
export interface PfsListElement<T, F> extends HTMLElement {
  controller: ListController<T, F>;
  config: () => ListConfig<T, F>;
  gate?: () => ListGate;
  onError?: () => void;
  /** Neu laden (nach Mutationen): Voll-Spinner + Fehler-Feedback. */
  reload(): Promise<void>;
  /** Voll-Rerender (z.B. nach optimistischer Mutation ohne Fetch). */
  rerender(): void;
}

const STYLES = `
${SHADOW_RESET_CSS}
/* container-type: die Toolbar (<pfs-row data-collapse>) fragt die Listenbreite ab
   und stapelt bei Enge (PFS-660, Query quert die pfs-card-Slot-Grenze). */
:host { display: block; container-type: inline-size; }
/* Card-Rahmen/Padding/Section-Gap kommen jetzt vom <pfs-card>-Primitive (PFS-658). */
.loading-area { min-height: 14rem; display: flex; align-items: center; justify-content: center; }
.empty-gate { text-align: center; padding: 3rem 1rem; color: var(--pfs-color-on-surface-muted); margin: 0; }
/* PFS-731: Create-Button nutzt die GETEILTE .btn-Quelle (BUTTON_CSS), nicht mehr
   den Eigenbau-.add-btn (zweite grüne Impl). EINE grüne Button-Quelle fleet-weit. */
${BUTTON_CSS}
`;

defineComponent({
  tagName: "pfs-list",
  mixins: [withShadow()],
  render(host, ctx) {
    const root = ctx.shadowRoot;
    if (!root) return;
    const self = host as PfsListElement<unknown, Record<string, unknown>>;

    const style = document.createElement("style");
    style.textContent = STYLES;

    // ── Zustand (Closure) ──────────────────────────────────────────────────
    let controller: ListController<unknown, Record<string, unknown>> | null = null;
    let configFn: (() => ListConfig<unknown, Record<string, unknown>>) | null = null;
    let gateFn: (() => ListGate) | null = null;
    let onErrorFn: (() => void) | null = null;
    let busy = false;
    let started = false;
    let unsub: (() => void) | null = null;

    const gate = (): ListGate => gateFn?.() ?? { ok: true };

    // Controller-Change → Tabelle IN-PLACE aktualisieren (Toolbar/Such-Fokus
    // unberuehrt, kein Blinken). Paginator: Fenster-Items ersetzen. Endless: Items
    // wachsen ueber loadMore/appendItems → hier nur Count + Sentinel-Flag pflegen.
    const syncTable = (): void => {
      const dt = root.querySelector<PfsDataTableElement<unknown>>("pfs-data-table");
      if (!dt || !controller) return;
      dt.dataset.count = String(controller.total);
      if (configFn?.().endless) {
        if (controller.hasMore) dt.dataset.hasMore = "";
        else dt.removeAttribute("data-has-more");
        return;
      }
      dt.items = controller.items;
    };

    // Endless-Scroll-Nachschub: naechste Seite anfordern (server-gestreamt bei
    // loadStrategy "page", sonst reines Enthuellen) + Delta scroll-erhaltend
    // anhaengen. Die AKTUELLE Tabelle nach dem await neu abfragen (ein
    // zwischenzeitliches paint() kann sie ersetzt haben) und nur die fehlenden
    // Zeilen anhaengen → robust gegen jede Render-Verschraenkung, kein Doppel-Append.
    const loadMore = async (): Promise<void> => {
      if (!controller) return;
      const before = root.querySelector<PfsDataTableElement<unknown>>("pfs-data-table");
      before?.setAttribute("data-loading-more", "");
      await controller.loadMore();
      const dt = root.querySelector<PfsDataTableElement<unknown>>("pfs-data-table");
      if (!dt) return;
      const shown = dt.items.length;
      if (shown < controller.items.length) dt.appendItems?.(controller.items.slice(shown));
      if (!controller.hasMore) dt.removeAttribute("data-has-more");
      dt.removeAttribute("data-loading-more");
    };

    // Rendert neu, ERHAELT Live-Tippstand + Fokus des Suchfelds (sonst gingen
    // waehrend eines langsamen Reloads weiter getippte Zeichen verloren).
    const rerenderPreservingSearch = (): void => {
      const old = root.querySelector<PfsSearchInputElement>("pfs-search-input[data-search-input]");
      const live = old?.value;
      paint();
      const el = root.querySelector<PfsSearchInputElement>("pfs-search-input[data-search-input]");
      if (!el) return;
      if (live !== undefined) el.value = live;
      el.focusEnd();
    };

    const runLoad = async (op: Promise<void>, opts?: { keepFocus?: boolean }): Promise<void> => {
      if (!opts?.keepFocus) {
        busy = true;
        paint();
      }
      await op;
      busy = false;
      if (controller?.error) onErrorFn?.();
      if (opts?.keepFocus) rerenderPreservingSearch();
      else paint();
    };

    const buildToolbar = (cfg: ListConfig<unknown, Record<string, unknown>>): HTMLElement | null => {
      const tb = cfg.toolbar;
      if (!tb || !controller) return null;
      const c = controller;
      // PFS-660: Toolbar = Komposition aus <pfs-row> (kein hand-gerollter Flex,
      // kein margin-Hack). Außen space-between (Aktion links, Tools rechts),
      // data-collapse → bei schmalem Container stapelt alles full-width.
      const toolbar = document.createElement("pfs-row");
      toolbar.setAttribute("data-collapse", "");
      toolbar.dataset.align = tb.add ? "between" : "end";

      if (tb.add) {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "btn " + (tb.add.variant ?? "success");
        btn.textContent = tb.add.label;
        btn.addEventListener("click", tb.add.onClick);
        toolbar.appendChild(btn);
      }
      if (tb.scope || tb.search || (tb.filters && tb.filters.length > 0)) {
        // Tools-Gruppe: rechts gebündelt; kollabiert selbst zur Spalte (Suche voll).
        const tools = document.createElement("pfs-row");
        tools.setAttribute("data-collapse", "");
        if (tb.scope) {
          const st = document.createElement("pfs-scope-toggle") as PfsScopeToggleElement;
          const key = tb.scope.filterKey;
          st.options = tb.scope.options;
          st.value = String(c.filters[key] ?? "");
          st.onChange = (v) => void runLoad(c.setFilter({ [key]: v }));
          tools.appendChild(st);
        }
        // Zusatz-Filter (z.B. Benutzer-Picker) in derselben Zeile neben scope/search.
        if (tb.filters) {
          for (const f of tb.filters) tools.appendChild(f);
        }
        if (tb.search) {
          const si = document.createElement("pfs-search-input") as PfsSearchInputElement;
          si.setAttribute("data-value", c.search);
          si.setAttribute("data-placeholder", tb.search.placeholder);
          si.setAttribute("data-search-input", "");
          si.onSearch = (q) => {
            if (q === c.search) return;
            void runLoad(c.setSearch(q), { keepFocus: true });
          };
          tools.appendChild(si);
        }
        toolbar.appendChild(tools);
      }
      return toolbar;
    };

    const buildTable = (cfg: ListConfig<unknown, Record<string, unknown>>): HTMLElement => {
      const dt = document.createElement("pfs-data-table") as PfsDataTableElement<unknown>;
      dt.dataset.count = String(controller?.total ?? 0);
      if (cfg.emptyTitle) dt.dataset.emptyTitle = cfg.emptyTitle();
      if (cfg.emptyIcon) dt.emptyIcon = cfg.emptyIcon;
      if (cfg.rowClass) dt.rowClass = cfg.rowClass;
      dt.columns = cfg.columns;
      dt.items = controller?.items ?? [];
      return dt;
    };

    const paint = (): void => {
      if (!controller || !configFn) {
        root.replaceChildren(style);
        return;
      }
      const cfg = configFn();

      // PFS-658: das <pfs-card>-Primitive (statt einer eigenen .card/.stack) —
      // damit ALLE Admin-Listen exakt wie Strikes/Formulare rendern (uniformes
      // Padding via --pfs-spacing-card-x/y + Section-Gap). Ein Card-SSOT.
      // PFS-760: sektionierter MFA-Look — data-sectioned (Divider vom Band) +
      // optionales Header-Icon + Body-Zone via <pfs-card-body>. Card auto-erkennt
      // den Header (PFS-756), kein slot="header" nötig.
      const card = document.createElement("pfs-card");
      card.setAttribute("data-sectioned", "");

      const header = document.createElement("pfs-card-header");
      header.dataset.title = cfg.title;
      // Subtitle lebt uniform im card-header (data-subtitle), nicht als loses <p>.
      if (cfg.subtitle) header.dataset.subtitle = cfg.subtitle;
      if (cfg.icon) header.innerHTML = cfg.icon;
      card.appendChild(header);

      // Body-Zone: Gate/Loading/Toolbar+Table+Paginator wohnen sektioniert unter
      // dem Header-Band (nicht als flache Card-Kinder).
      const body = document.createElement("pfs-card-body");

      const g = gate();
      if (!g.ok) {
        const msg = document.createElement("p");
        msg.className = "empty-gate";
        msg.textContent = g.message ?? "";
        body.appendChild(msg);
      } else if (busy) {
        const area = document.createElement("div");
        area.className = "loading-area";
        const li = document.createElement("pfs-loading-indicator");
        li.setAttribute("data-variant", "main");
        if (cfg.loadingText) li.setAttribute("data-text", cfg.loadingText);
        area.appendChild(li);
        body.appendChild(area);
      } else {
        const toolbar = buildToolbar(cfg);
        if (toolbar) body.appendChild(toolbar);
        const dt = buildTable(cfg);
        body.appendChild(dt);
        if (cfg.endless) {
          // Endless-Scroll: data-table-Sentinel + loadMore (KEIN Paginator).
          if (controller.hasMore) dt.dataset.hasMore = "";
          dt.addEventListener("pfs:load-more", () => void loadMore());
        } else {
          const pg = document.createElement("pfs-paginator") as PfsPaginatorElement;
          if (cfg.pageSizes) pg.dataset.pageSizes = cfg.pageSizes;
          pg.controller = controller;
          body.appendChild(pg);
        }
      }

      card.appendChild(body);
      root.replaceChildren(style, card);
    };

    // Beim Setzen von controller+config: einmal subscriben + initial rendern/laden.
    const maybeStart = (): void => {
      if (started || !controller || !configFn) return;
      started = true;
      unsub = controller.subscribe(syncTable);
      paint();
      if (gate().ok) void runLoad(controller.load());
    };

    // ── Properties (upgrade-fest) + imperative API ─────────────────────────
    const preController = takeOwnProperty<ListController<unknown, Record<string, unknown>>>(host, "controller");
    const preConfig = takeOwnProperty<() => ListConfig<unknown, Record<string, unknown>>>(host, "config");
    const preGate = takeOwnProperty<() => ListGate>(host, "gate");
    const preOnError = takeOwnProperty<() => void>(host, "onError");

    Object.defineProperty(host, "controller", {
      configurable: true,
      get: () => controller,
      set: (v: ListController<unknown, Record<string, unknown>>) => {
        controller = v ?? null;
        maybeStart();
      },
    });
    Object.defineProperty(host, "config", {
      configurable: true,
      get: () => configFn,
      set: (v: () => ListConfig<unknown, Record<string, unknown>>) => {
        configFn = v ?? null;
        if (started) paint();
        else maybeStart();
      },
    });
    Object.defineProperty(host, "gate", {
      configurable: true,
      get: () => gateFn,
      set: (v: () => ListGate) => {
        gateFn = v ?? null;
        if (started) paint();
      },
    });
    Object.defineProperty(host, "onError", {
      configurable: true,
      get: () => onErrorFn,
      set: (v: () => void) => {
        onErrorFn = v ?? null;
      },
    });

    self.reload = () => (controller ? runLoad(controller.reload()) : Promise.resolve());
    self.rerender = () => paint();

    if (preOnError !== undefined) self.onError = preOnError;
    if (preGate !== undefined) self.gate = preGate;
    if (preConfig !== undefined) self.config = preConfig;
    if (preController !== undefined) self.controller = preController;

    ctx.cleanup.push(() => unsub?.());

    paint();
  },
});
